import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL } from "../config/api";
import {
  FileText,
  Shield,
  CreditCard,
  Building2,
  Plus,
  Search,
  Download,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Edit,
  Eye,
  Calendar,
  Globe,
  Banknote,
  Users,
  FileCheck,
  RefreshCw,
} from "lucide-react";


const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  active: "bg-green-100 text-green-800",
  approved: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-800",
  terminated: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800",
};

export default function CRMDocumentsPage() {
  const [activeTab, setActiveTab] = useState("service");
  const [loading, setLoading] = useState(false);
  
  // Service Agreements
  const [serviceAgreements, setServiceAgreements] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    company_id: "",
    company_name: "",
    agreement_type: "agency",
    effective_date: new Date().toISOString().slice(0, 10),
    expiry_date: "",
    payment_terms_days: 30,
    auto_renewal: true,
    renewal_notice_days: 30,
    territory: "",
    services_provided: "",
    governing_law: "England and Wales",
    arbitration_location: "ICC London",
    status: "draft",
    notes: "",
  });
  
  // KYC Forms
  const [kycForms, setKycForms] = useState([]);
  const [showKycModal, setShowKycModal] = useState(false);
  const [kycForm, setKycForm] = useState({
    company_id: "",
    company_name: "",
    legal_name: "",
    registration_number: "",
    vat_number: "",
    registered_address: "",
    business_address: "",
    country_of_incorporation: "",
    year_established: "",
    business_type: "",
    industry_sector: "",
    primary_contact_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    finance_contact_name: "",
    finance_contact_email: "",
    bank_name: "",
    bank_account_number: "",
    bank_swift: "",
    bank_iban: "",
    status: "pending",
    notes: "",
  });
  
  // Company search
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  const token = localStorage.getItem("token");

  // Fetch service agreements
  const fetchServiceAgreements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/service-agreements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServiceAgreements(response.data.agreements);
    } catch (error) {
      toast.error("Failed to load service agreements");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch KYC forms
  const fetchKycForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/kyc-forms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKycForms(response.data.forms);
    } catch (error) {
      toast.error("Failed to load KYC forms");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Search companies
  const searchCompanies = async (query) => {
    if (!query || query.length < 2) {
      setCompanyResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/crm/companies?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanyResults(response.data.companies || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      console.error("Company search failed", error);
    }
  };

  // Save service agreement
  const saveServiceAgreement = async () => {
    if (!serviceForm.company_id || !serviceForm.company_name) {
      toast.error("Please select a company");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/crm/service-agreements`, serviceForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Service agreement created");
      setShowServiceModal(false);
      setServiceForm({
        company_id: "",
        company_name: "",
        agreement_type: "agency",
        effective_date: new Date().toISOString().slice(0, 10),
        expiry_date: "",
        payment_terms_days: 30,
        auto_renewal: true,
        renewal_notice_days: 30,
        territory: "",
        services_provided: "",
        governing_law: "England and Wales",
        arbitration_location: "ICC London",
        status: "draft",
        notes: "",
      });
      fetchServiceAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create service agreement");
    }
  };

  // Save KYC form
  const saveKycForm = async () => {
    if (!kycForm.company_id || !kycForm.company_name || !kycForm.legal_name) {
      toast.error("Please fill in required fields");
      return;
    }
    
    try {
      await axios.post(`${API_URL}/crm/kyc-forms`, kycForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("KYC form created");
      setShowKycModal(false);
      setKycForm({
        company_id: "",
        company_name: "",
        legal_name: "",
        registration_number: "",
        vat_number: "",
        registered_address: "",
        business_address: "",
        country_of_incorporation: "",
        year_established: "",
        business_type: "",
        industry_sector: "",
        primary_contact_name: "",
        primary_contact_email: "",
        primary_contact_phone: "",
        finance_contact_name: "",
        finance_contact_email: "",
        bank_name: "",
        bank_account_number: "",
        bank_swift: "",
        bank_iban: "",
        status: "pending",
        notes: "",
      });
      fetchKycForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create KYC form");
    }
  };

  // Update agreement/form status
  const updateStatus = async (type, id, newStatus) => {
    try {
      const endpoint = type === "service" 
        ? `${API_URL}/crm/service-agreements/${id}`
        : `${API_URL}/crm/kyc-forms/${id}`;
      
      await axios.put(endpoint, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: newStatus },
      });
      toast.success(`Status updated to ${newStatus}`);
      type === "service" ? fetchServiceAgreements() : fetchKycForms();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    fetchServiceAgreements();
    fetchKycForms();
  }, [fetchServiceAgreements, fetchKycForms]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 rounded-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-8 w-8" />
                <h1 className="text-3xl font-bold">CRM Documents</h1>
              </div>
              <p className="text-blue-100">
                Service Agreements, KYC Forms & Credit Agreements
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowServiceModal(true)}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="new-service-agreement-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Service Agreement
              </Button>
              <Button
                onClick={() => setShowKycModal(true)}
                className="bg-white/20 hover:bg-white/30 border border-white/30"
                data-testid="new-kyc-btn"
              >
                <Shield className="h-4 w-4 mr-2" />
                New KYC Form
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "service", label: "Service Agreements", icon: FileCheck, count: serviceAgreements.length },
            { id: "kyc", label: "KYC Forms", icon: Shield, count: kycForms.length },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2"
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-xs">
                {tab.count}
              </span>
            </Button>
          ))}
        </div>

        {/* Service Agreements Tab */}
        {activeTab === "service" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-900">Service / Agency Agreements</h3>
                <Button onClick={() => setShowServiceModal(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New Agreement
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Company</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Type</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Effective Date</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Expiry</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Payment Terms</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceAgreements.map((agr) => (
                    <tr key={agr.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{agr.company_name}</div>
                        <div className="text-xs text-slate-500">{agr.territory || 'Global'}</div>
                      </td>
                      <td className="p-3 text-center capitalize">{agr.agreement_type}</td>
                      <td className="p-3 text-center text-sm">{agr.effective_date?.slice(0, 10)}</td>
                      <td className="p-3 text-center text-sm">
                        {agr.auto_renewal ? (
                          <span className="text-green-600">Auto-renewal</span>
                        ) : (
                          agr.expiry_date?.slice(0, 10) || '-'
                        )}
                      </td>
                      <td className="p-3 text-center">{agr.payment_terms_days} days</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[agr.status]}`}>
                          {agr.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          {agr.status === "draft" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus("service", agr.id, "active")}
                              title="Activate"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {agr.status === "active" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => updateStatus("service", agr.id, "terminated")}
                              title="Terminate"
                            >
                              <XCircle className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {serviceAgreements.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400">
                        No service agreements found. Create one to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* KYC Forms Tab */}
        {activeTab === "kyc" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-slate-900">KYC (Know Your Customer) Forms</h3>
                <Button onClick={() => setShowKycModal(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  New KYC Form
                </Button>
              </div>
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Company</th>
                    <th className="text-left p-3 text-xs font-medium text-slate-500">Legal Name</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Country</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">VAT</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Sanctions Check</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Status</th>
                    <th className="text-center p-3 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {kycForms.map((form) => (
                    <tr key={form.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{form.company_name}</div>
                      </td>
                      <td className="p-3 text-sm">{form.legal_name}</td>
                      <td className="p-3 text-center text-sm">{form.country_of_incorporation || '-'}</td>
                      <td className="p-3 text-center text-sm">{form.vat_number || '-'}</td>
                      <td className="p-3 text-center">
                        {form.sanctions_check_passed ? (
                          <CheckCircle className="h-5 w-5 text-green-600 mx-auto" />
                        ) : (
                          <Clock className="h-5 w-5 text-yellow-600 mx-auto" />
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[form.status]}`}>
                          {form.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-1">
                          {form.status === "pending" && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateStatus("kyc", form.id, "approved")}
                                title="Approve"
                              >
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateStatus("kyc", form.id, "rejected")}
                                title="Reject"
                              >
                                <XCircle className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {kycForms.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-slate-400">
                        No KYC forms found. Create one to start customer verification.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {/* Service Agreement Modal */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              New Service / Agency Agreement
            </h3>
            
            <div className="space-y-4">
              {/* Company Search */}
              <div className="relative">
                <Label className="text-xs text-slate-500">Company *</Label>
                <Input
                  value={serviceForm.company_name}
                  onChange={(e) => {
                    setServiceForm((f) => ({ ...f, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  placeholder="Search company..."
                  className="mt-1"
                />
                {showCompanyDropdown && companyResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {companyResults.map((company) => (
                      <div
                        key={company.id}
                        className="p-2 hover:bg-slate-100 cursor-pointer"
                        onClick={() => {
                          setServiceForm((f) => ({
                            ...f,
                            company_id: company.id,
                            company_name: company.name,
                          }));
                          setShowCompanyDropdown(false);
                        }}
                      >
                        <div className="font-medium">{company.name}</div>
                        {company.country && <div className="text-xs text-slate-500">{company.country}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Agreement Type</Label>
                  <select
                    value={serviceForm.agreement_type}
                    onChange={(e) => setServiceForm((f) => ({ ...f, agreement_type: e.target.value }))}
                    className="w-full mt-1 h-10 px-3 border rounded-lg"
                  >
                    <option value="agency">Agency Agreement</option>
                    <option value="service">Service Agreement</option>
                    <option value="partnership">Partnership Agreement</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <select
                    value={serviceForm.status}
                    onChange={(e) => setServiceForm((f) => ({ ...f, status: e.target.value }))}
                    className="w-full mt-1 h-10 px-3 border rounded-lg"
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending Signature</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Effective Date *</Label>
                  <Input
                    type="date"
                    value={serviceForm.effective_date}
                    onChange={(e) => setServiceForm((f) => ({ ...f, effective_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Expiry Date (leave empty for auto-renewal)</Label>
                  <Input
                    type="date"
                    value={serviceForm.expiry_date}
                    onChange={(e) => setServiceForm((f) => ({ ...f, expiry_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Payment Terms (days)</Label>
                  <Input
                    type="number"
                    value={serviceForm.payment_terms_days}
                    onChange={(e) => setServiceForm((f) => ({ ...f, payment_terms_days: parseInt(e.target.value) || 30 }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Renewal Notice (days)</Label>
                  <Input
                    type="number"
                    value={serviceForm.renewal_notice_days}
                    onChange={(e) => setServiceForm((f) => ({ ...f, renewal_notice_days: parseInt(e.target.value) || 30 }))}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={serviceForm.auto_renewal}
                    onChange={(e) => setServiceForm((f) => ({ ...f, auto_renewal: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <Label>Auto-renewal</Label>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Territory / Geographic Scope</Label>
                <Input
                  value={serviceForm.territory}
                  onChange={(e) => setServiceForm((f) => ({ ...f, territory: e.target.value }))}
                  placeholder="e.g., Slovenia, Croatia, Serbia"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Governing Law</Label>
                  <Input
                    value={serviceForm.governing_law}
                    onChange={(e) => setServiceForm((f) => ({ ...f, governing_law: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Arbitration Location</Label>
                  <Input
                    value={serviceForm.arbitration_location}
                    onChange={(e) => setServiceForm((f) => ({ ...f, arbitration_location: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Notes</Label>
                <textarea
                  value={serviceForm.notes}
                  onChange={(e) => setServiceForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full mt-1 p-3 border rounded-lg resize-none"
                  rows={3}
                  placeholder="Additional notes about this agreement..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowServiceModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveServiceAgreement} data-testid="save-service-agreement-btn">
                Create Agreement
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* KYC Form Modal */}
      {showKycModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              New KYC Form
            </h3>
            
            <div className="space-y-6">
              {/* Company Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Company Identification</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative col-span-2">
                    <Label className="text-xs text-slate-500">Company (from CRM) *</Label>
                    <Input
                      value={kycForm.company_name}
                      onChange={(e) => {
                        setKycForm((f) => ({ ...f, company_name: e.target.value }));
                        searchCompanies(e.target.value);
                      }}
                      placeholder="Search company..."
                      className="mt-1"
                    />
                    {showCompanyDropdown && companyResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {companyResults.map((company) => (
                          <div
                            key={company.id}
                            className="p-2 hover:bg-slate-100 cursor-pointer"
                            onClick={() => {
                              setKycForm((f) => ({
                                ...f,
                                company_id: company.id,
                                company_name: company.name,
                                legal_name: company.name,
                                vat_number: company.vat_number || "",
                                registered_address: company.address || "",
                                country_of_incorporation: company.country || "",
                              }));
                              setShowCompanyDropdown(false);
                            }}
                          >
                            {company.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Legal Name *</Label>
                    <Input
                      value={kycForm.legal_name}
                      onChange={(e) => setKycForm((f) => ({ ...f, legal_name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Registration Number</Label>
                    <Input
                      value={kycForm.registration_number}
                      onChange={(e) => setKycForm((f) => ({ ...f, registration_number: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">VAT Number</Label>
                    <Input
                      value={kycForm.vat_number}
                      onChange={(e) => setKycForm((f) => ({ ...f, vat_number: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Country of Incorporation</Label>
                    <Input
                      value={kycForm.country_of_incorporation}
                      onChange={(e) => setKycForm((f) => ({ ...f, country_of_incorporation: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Registered Address</Label>
                    <Input
                      value={kycForm.registered_address}
                      onChange={(e) => setKycForm((f) => ({ ...f, registered_address: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Contact Persons</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Primary Contact</Label>
                    <Input
                      value={kycForm.primary_contact_name}
                      onChange={(e) => setKycForm((f) => ({ ...f, primary_contact_name: e.target.value }))}
                      placeholder="Name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input
                      type="email"
                      value={kycForm.primary_contact_email}
                      onChange={(e) => setKycForm((f) => ({ ...f, primary_contact_email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input
                      value={kycForm.primary_contact_phone}
                      onChange={(e) => setKycForm((f) => ({ ...f, primary_contact_phone: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Finance Contact</Label>
                    <Input
                      value={kycForm.finance_contact_name}
                      onChange={(e) => setKycForm((f) => ({ ...f, finance_contact_name: e.target.value }))}
                      placeholder="Name"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Finance Email</Label>
                    <Input
                      type="email"
                      value={kycForm.finance_contact_email}
                      onChange={(e) => setKycForm((f) => ({ ...f, finance_contact_email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Banking Details */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm text-slate-700 mb-3">Banking Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Bank Name</Label>
                    <Input
                      value={kycForm.bank_name}
                      onChange={(e) => setKycForm((f) => ({ ...f, bank_name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Account Number</Label>
                    <Input
                      value={kycForm.bank_account_number}
                      onChange={(e) => setKycForm((f) => ({ ...f, bank_account_number: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">SWIFT Code</Label>
                    <Input
                      value={kycForm.bank_swift}
                      onChange={(e) => setKycForm((f) => ({ ...f, bank_swift: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">IBAN</Label>
                    <Input
                      value={kycForm.bank_iban}
                      onChange={(e) => setKycForm((f) => ({ ...f, bank_iban: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowKycModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveKycForm} data-testid="save-kyc-btn">
                Create KYC Form
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
